import { useState, useEffect, useMemo } from 'react';
import { Tablet } from 'lucide-react';
import { ClubPadBanner } from '../tournament';
import { CourtPadCard } from './CourtPadCard';
import { deviceFillBoxStyle } from './deviceFill';

export interface CourtPadTabProps {
  /** The tournament whose matches drive the Banepad screens (passed as `t` internally). */
  tournament: any;
  /** Callback to record a result (matchId, s1, s2, source). */
  onRecordResult?: any;
  /** Submission phase for the match being sent — see CourtPadCard. */
  submitStatus?: { phase: 'idle' | 'sending' | 'done'; matchId: string | null };
  /** Persists the in-progress (live) points without finalising: (matchId, { score1, score2 }). */
  onLiveScore?: any;
  /** Callback when a player chip is clicked. */
  onPlayerClick?: any;
  /** When set, render only this single court's pad card (used for one-screen-per-bane). */
  onlyCourt?: any;
  /**
   * Match numbers computed elsewhere, keyed by match id.
   *
   * «Kamp 1» is the first match played ANYWHERE that day, so a pad holding only
   * its own court cannot derive it (#325/#328). A pad fed by `/pad/state` is
   * given the numbers; every other caller passes nothing and they are computed
   * below from the whole tournament, exactly as before.
   */
  globalMatchNumber?: Record<string, number> | null;
  /** Club scoreboard computed elsewhere — forwarded to <ClubPadBanner>. */
  clubStandings?: { standings: any; leader: any } | null;
  /**
   * Live clock from the parent (the pad shell's, which is also what resolved
   * which session belongs on this court). Falls back to an internal 1s tick
   * when omitted, so standalone renders and the tournament-view call sites are
   * unchanged.
   */
  now?: any;
  /** Device kiosk mode: forwarded to <CourtPadCard> to drop its size caps. */
  fill?: boolean;
  /**
   * The live `Padel24Engine` wiring object. Forwarded
   * to <ClubPadBanner> which resolves the per-format standings engine off it.
   * (deviation: monolith read this via closure.)
   */
  Padel24Engine: any;
  /**
   * Plays an end-sound by key — shared audio helper (Web Audio).
   * Forwarded to <CourtPadCard>, which is the actual call-site.
   * (deviation: monolith read this via closure.)
   */
  playEndSound: (soundType?: string) => void;
}

/**
 * Banepad tab — discovers the courts used by the tournament, builds per-court
 * chronological match feeds (with breaks woven in) and renders one <CourtPadCard>
 * "iPad" per court, plus a live clock indicator. `onlyCourt` renders a single pad.
 * Extracted verbatim from the monolith (legacy/App.jsx); markup/logic unchanged.
 * Takes `Padel24Engine` (forwarded to <ClubPadBanner>) and `playEndSound`
 * (forwarded to <CourtPadCard>) as props.
 */
export function CourtPadTab({
  tournament: t,
  onRecordResult,
  submitStatus,
  onLiveScore,
  onPlayerClick,
  onlyCourt = null,
  globalMatchNumber: globalMatchNumberProp = null,
  clubStandings = null,
  now: nowProp,
  fill = false,
  Padel24Engine,
  playEndSound,
}: CourtPadTabProps) {
  // THE PARENT'S CLOCK WINS.
  //
  // The pad shell resolves WHICH session belongs on this court against its own
  // 1s clock and threads it down; this component used to ignore it and start a
  // second one. The two agree in production — both read the wall clock — so
  // nothing visible changed, but the chain's stated invariant ("the pad matches
  // the resolved context") was not actually enforced for the most-used format,
  // and an injected clock silently had no effect on the match feed. That made
  // this untestable and would matter the moment a server-resolved time is in
  // play. Same fallback pattern as BanepadDeviceDisplay.
  const [ownNow, setOwnNow] = useState(() => new Date());
  useEffect(() => {
    if (nowProp) return undefined;
    const id = setInterval(() => setOwnNow(new Date()), 1000);
    return () => clearInterval(id);
  }, [nowProp]);
  const now = nowProp ?? ownNow;

  // Find all courts used in the tournament
  const courts = useMemo(() => {
    const set = new Set();
    (t.matches || []).forEach((m) => {
      if (m.court) set.add(m.court);
    });
    // deviation: type-only cast (monolith is @ts-nocheck) — Set<unknown> yields unknown operands
    return Array.from(set).sort((a, b) => (a as number) - (b as number));
  }, [t.matches]);

  // Helper: is a match "now" based on time
  // A match counts as "in progress" if the current time is between match start and match end
  const matchDurations = useMemo(() => {
    const map = {};
    (t.config?.days || []).forEach((d) => {
      map[d.id] = d.matchDuration || 30;
    });
    return map;
  }, [t.config]);

  // Breaks per date — flat list with start/end minutes since midnight.
  // Each break has date (YYYY-MM-DD), startMin, endMin, label.
  // Banepad uses this to show a "Pause" block in the match feed.
  const breaksByDate = useMemo(() => {
    const result = {};
    (t.config?.days || []).forEach((d) => {
      if (!d.date || !Array.isArray(d.breaks)) return;
      result[d.date] = d.breaks
        .filter((b) => (b.mode || 'time') === 'time' && b.start)
        .map((b) => {
          // start is an HH:MM string
          const [h, m] = (b.start || '').split(':').map((n) => parseInt(n, 10) || 0);
          const startMin = h * 60 + m;
          return {
            id: b.id,
            label: b.label || 'Pause',
            startMin,
            endMin: startMin + (b.durationMin || 30),
            durationMin: b.durationMin || 30,
          };
        })
        .sort((a, b) => a.startMin - b.startMin);
    });
    return result;
  }, [t.config]);

  // Current date and minutes since midnight
  // LOCAL date, never toISOString (docs/architecture/DOMAIN_RULES.md). Every `match.date` is
  // a local YYYY-MM-DD, and toISOString is UTC — so at a positive UTC offset the
  // two disagree between midnight and the offset. In Oslo that meant a match
  // running at 00:30 was compared against YESTERDAY, failed the `m.date ===
  // todayStr` checks below, and the pad showed «NESTE KAMP» for a match that
  // was half over (#323). Invisible on a UTC CI runner, which is why it needs
  // the timezone-pinned test.
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  // Global match number across ALL courts — sorted chronologically
  // So that Match 1 is the first one played regardless of court.
  const computedMatchNumber = useMemo(() => {
    const sorted = [...(t.matches || [])].sort((a, b) => {
      const da = a.date || '',
        db = b.date || '';
      if (da !== db) return da < db ? -1 : 1;
      if ((a.timeMinutes ?? 9999) !== (b.timeMinutes ?? 9999)) {
        return (a.timeMinutes ?? 9999) - (b.timeMinutes ?? 9999);
      }
      // If same time and date, sort by court (match 1 before match 2 at the same time)
      return (a.court || 0) - (b.court || 0);
    });
    const map = {};
    sorted.forEach((m, i) => {
      map[m.id] = i + 1;
    });
    return map;
  }, [t.matches]);
  // The projected numbers win when present — see the prop's comment.
  const globalMatchNumber = globalMatchNumberProp ?? computedMatchNumber;

  // Per court: full sorted match list — all done + in-progress + future
  // This gives us a "scroll-bar feed" where the in-progress match is centered
  const courtData = useMemo(() => {
    return courts.map((court) => {
      const courtMatches = (t.matches || []).filter((m) => m.court === court);

      // Sort chronologically
      const sorted = [...courtMatches].sort((a, b) => {
        const da = a.date || '',
          db = b.date || '';
        if (da !== db) return da < db ? -1 : 1;
        return (a.timeMinutes ?? 9999) - (b.timeMinutes ?? 9999);
      });

      return { court, matches: sorted };
    });
  }, [courts, t.matches]);

  // One physical pad = one court. onlyCourt → render ONLY that court's card (without wrapper).
  // Club score banner pinned INSIDE each pad (null for non-club).
  const clubBanner = (
    <ClubPadBanner tournament={t} inPad Padel24Engine={Padel24Engine} precomputed={clubStandings} />
  );

  if (onlyCourt != null) {
    const entry = courtData.find((c) => Number(c.court) === Number(onlyCourt));
    if (!entry) {
      return (
        <div
          className="rounded-2xl flex items-center justify-center"
          style={{
            aspectRatio: '4 / 3',
            background: 'linear-gradient(165deg, #0a0b10 0%, #14161f 100%)',
            color: 'rgba(230,251,255,0.5)',
            ...deviceFillBoxStyle(fill),
          }}
        >
          <div className="text-center">
            <Tablet size={28} className="mx-auto mb-2 opacity-40" />
            <div className="font-mono text-[11px] uppercase tracking-wider">
              Ingen kamper på bane {onlyCourt}
            </div>
          </div>
        </div>
      );
    }
    return (
      <CourtPadCard
        court={entry.court}
        tournamentId={t.id}
        formatType={t.type || 'padel24'}
        matches={entry.matches}
        globalMatchNumber={globalMatchNumber}
        now={now}
        nowMinutes={nowMinutes}
        todayStr={todayStr}
        matchDurations={matchDurations}
        breaksByDate={breaksByDate}
        defaultFlowMode={t.config?.padFlowMode || 'result'}
        players={t.players}
        onRecordResult={onRecordResult}
        submitStatus={submitStatus}
        onLiveScore={onLiveScore}
        onPlayerClick={onPlayerClick}
        clubBanner={clubBanner}
        playEndSound={playEndSound}
        fill={fill}
      />
    );
  }

  if (courts.length === 0) {
    return (
      <div className="card p-12 text-center text-ink-3">
        <Tablet size={32} className="mx-auto mb-3 opacity-50" />
        <div className="font-display text-lg mb-1">Ingen baner ennå</div>
        <div className="text-sm">Turneringens kampplan må trekkes før banepadene aktiveres</div>
      </div>
    );
  }

  return (
    <div>
      {/* Live indicator */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-deep opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-lime-deep"></span>
          </span>
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
            Live ·{' '}
            {now.toLocaleTimeString('no-NO', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
          {courts.length} {courts.length === 1 ? 'bane' : 'baner'}
        </span>
      </div>

      {/* 1 card per row — centered with a larger fixed width for consistent display */}
      <div className="flex flex-col items-center gap-6">
        {courtData.map(({ court, matches }) => (
          <CourtPadCard
            // deviation: type-only cast (monolith is @ts-nocheck) — court inferred unknown for Key
            key={court as any}
            court={court}
            tournamentId={t.id}
            formatType={t.type || 'padel24'}
            matches={matches}
            globalMatchNumber={globalMatchNumber}
            now={now}
            nowMinutes={nowMinutes}
            todayStr={todayStr}
            matchDurations={matchDurations}
            breaksByDate={breaksByDate}
            defaultFlowMode={t.config?.padFlowMode || 'result'}
            players={t.players}
            onRecordResult={onRecordResult}
            submitStatus={submitStatus}
            onLiveScore={onLiveScore}
            onPlayerClick={onPlayerClick}
            clubBanner={clubBanner}
            playEndSound={playEndSound}
          />
        ))}
      </div>
    </div>
  );
}
